import { operationIds } from "@gen/app-access-config";
import type {
  IClientResponse2,
  IClientWebhook,
  IUpdateClientBody,
} from "@gen/app-query";
import {
  CoreQuery,
  CreateAdminClientWebhookBody,
  CreateClientCustomizationBody,
  UpdateAdminClientWebhookBody,
  UpdateClientApiKeyBody,
  UpdateClientBody,
  UpdateClientCustomizationBody,
} from "@gen/app-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { HeadingContainer } from "~/components/heading_container";
import { HideableText } from "~/components/hideable_text";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import {
  SectionSideNav,
  type SectionSideNavItem,
} from "~/components/section_side_nav";
import { TextInput } from "~/components/text_input";
import { useAuthOperations } from "~/contexts/auth_query";
import { useConfig } from "~/contexts/config";
import { clientRedirectUriSchema } from "~/entities/create_client";

import { AccountStatusBanner } from "../AccountStatusBanner";
import { ApiKeyEditor } from "../ApiKeyEditor";
import { CustomizationEditor } from "../CustomizationEditor";
import { EncodedClientKeyDialog } from "../EncodedClientKeyDialog";
import { F2FTimelines } from "../F2FTimelines";
import { OnlineTimelines } from "../OnlineTimelines";
import {
  RedirectURIsEditorWithIds,
  type RedirectUriWithId,
} from "../RedirectURIsEditor";
import { RolesAndPermissions } from "../RolesAndPermissions";
import { WebhooksEditor } from "../WebhooksEditor";
import { ClientExportDialog } from "./ClientExportDialog";
import { ClientMetadataEditor } from "./ClientMetadataEditor";

type UpdateClientFormValues = Omit<IUpdateClientBody, "redirect_uris"> & {
  id: string;
};

export function Client() {
  const { canExecuteAdminOperation } = useAuthOperations();
  const { localization } = useConfig();
  // RedirectURIs are updated via the updateClient operation.
  const canUpdateRedirectURIs = canExecuteAdminOperation(
    operationIds["core_admin.updateClient"],
  );

  const shouldShowAdminClientTimelines =
    canExecuteAdminOperation(
      operationIds["core_admin.getAdminClientF2fTimelines"],
    ) ||
    canExecuteAdminOperation(
      operationIds["core_admin.getAdminClientTimelines"],
    );

  const clientId = useParams({
    from: "/admin/clients/$clientId",
    select(params) {
      return params.clientId;
    },
  });
  const {
    data: clientData,
    isFetching: isClientFetching,
    refetch: refetchClient,
  } = CoreQuery.useAdmin_getClientQuery(clientId);
  const { data: keycloakClientSecretData } =
    CoreQuery.useAdmin_getKeycloakClientSecretQuery(clientId, {
      enabled: canExecuteAdminOperation(
        operationIds["core_admin.getKeycloakClientSecret"],
      ),
    });
  const {
    data: apiKeyData,
    isFetching: isApiKeyFetching,
    refetch: refetchApiKey,
  } = CoreQuery.useAdmin_getClientApiKeyQuery(clientId, {
    enabled: canExecuteAdminOperation(
      operationIds["core_admin.getClientApiKey"],
    ),
  });
  const { data: webhooksData, isFetching: isWebhooksFetching } =
    CoreQuery.useAdmin_getAdminClientWebhooksQuery(clientId);
  const {
    data: customizationData,
    isFetching: isCustomizationFetching,
    refetch: refetchCustomization,
  } = CoreQuery.useAdmin_getClientCustomizationQuery(clientId);

  const [isEncodedDialogOpened, setEncodedDialog] = useState(false);
  const [editedWebhooks, setEditedWebhooks] = useState<IClientWebhook[]>([]);
  const [isExportDialogOpen, setExportDialogOpen] = useState(false);
  const [stableRedirectUris, setStableRedirectUris] = useState<
    RedirectUriWithId[]
  >([]);

  const queryClient = useQueryClient();

  const { mutate: createWebhook, isPending: isCreatingWebhook } =
    CoreQuery.useAdmin_createAdminClientWebhookMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getAdminClientWebhooksQueryKey(clientId),
        });
      },
      meta: {
        success: "Webhook created",
      },
    });

  const { mutate: updateWebhook, isPending: isUpdatingWebhook } =
    CoreQuery.useAdmin_updateAdminClientWebhookMutationWithParameters({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getAdminClientWebhooksQueryKey(clientId),
        });
      },
      meta: {
        success: "Webhook updated",
      },
    });

  const { mutate: deleteWebhook, isPending: isDeletingWebhook } =
    CoreQuery.useAdmin_deleteAdminClientWebhookMutationWithParameters({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getAdminClientWebhooksQueryKey(clientId),
        });
      },
      meta: {
        success: "Webhook deleted",
      },
    });

  const { mutate: setApiKey, isPending: isApiKeyUpdatePending } =
    CoreQuery.useAdmin_updateClientApiKeyMutation(clientId, {
      onSuccess: () => {
        refetchApiKey();
      },
      meta: {
        success: "API key updated",
      },
    });

  const { mutate: createCustomization, isPending: isCreatingCustomization } =
    CoreQuery.useAdmin_createClientCustomizationMutation(clientId, {
      onSuccess: () => {
        refetchCustomization();
      },
      meta: {
        success: "Customization created",
      },
    });

  const { mutate: updateCustomization, isPending: isUpdatingCustomization } =
    CoreQuery.useAdmin_updateClientCustomizationMutation(clientId, {
      onSuccess: () => {
        refetchCustomization();
      },
      meta: {
        success: "Customization updated",
      },
    });

  useEffect(() => {
    if (clientData?.name) {
      document.title = `Client Settings: ${clientData.name}`;
    }
  }, [clientData?.name]);

  useEffect(() => {
    if (!clientData?.redirect_uris) {
      return;
    }

    setStableRedirectUris((current) => {
      if (current.length === clientData.redirect_uris.length) {
        return current.map((item, index) => ({
          ...item,
          uri: clientData.redirect_uris[index],
        }));
      }

      return clientData.redirect_uris.map((uri, index) => ({
        id: current[index]?.id ?? crypto.randomUUID(),
        uri,
      }));
    });
  }, [clientData?.redirect_uris]);

  const formValues = useMemo<UpdateClientFormValues>(
    () => ({
      base_url: clientData?.base_url ?? "",
      contact_email: clientData?.contact_email ?? "",
      id: clientData?.id ?? "",
      name: clientData?.name ?? "",
    }),
    [clientData],
  );

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm<UpdateClientFormValues>({
    defaultValues: formValues,
  });

  useEffect(() => {
    reset(formValues);
  }, [formValues, reset]);

  const { mutate: updateClient, isPending: isUpdatingClient } =
    CoreQuery.useAdmin_updateClientMutation(clientId, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getClientQueryKey(clientId),
        });
        // Accept the just-submitted values as the new clean baseline.
        reset(getValues());
      },
      meta: {
        success: "Client updated",
      },
    });

  const renderField = (
    name: keyof UpdateClientFormValues,
    placeholder: string,
    label: string,
    multiline = false,
    disabled = false,
  ) => (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field }) => (
        <TextInput
          {...field}
          name={String(field.name)}
          placeholder={placeholder}
          label={label}
          clearable={!disabled}
          multiline={multiline}
          disabled={disabled}
          onClear={() => field.onChange("")}
          sx={{
            flexGrow: 1,
          }}
          data-testid={`client-${name}-input`}
        />
      )}
    />
  );

  const toUpdateClient = (
    values: UpdateClientFormValues,
    redirectUris: RedirectUriWithId[],
  ): IUpdateClientBody => {
    return {
      base_url: values.base_url,
      contact_email: values.contact_email,
      name: values.name,
      redirect_uris: redirectUris
        .map((r) => r.uri.trim())
        .filter((uri) => uri !== ""),
    };
  };

  const onSubmit = (data: UpdateClientFormValues) => {
    updateClient(
      UpdateClientBody.fromJS(toUpdateClient(data, stableRedirectUris)),
    );
  };

  const handleRedirectUriSave = useCallback(
    (row: RedirectUriWithId) => {
      const existingIndex = stableRedirectUris.findIndex(
        (r) => r.id === row.id,
      );
      const updatedUris =
        existingIndex >= 0
          ? stableRedirectUris.map((r) => (r.id === row.id ? row : r))
          : [...stableRedirectUris, row];

      updateClient(
        UpdateClientBody.fromJS(toUpdateClient(getValues(), updatedUris)),
      );
    },
    [stableRedirectUris, updateClient, getValues],
  );

  const handleRedirectUriDelete = useCallback(
    (row: RedirectUriWithId) => {
      const updatedUris = stableRedirectUris.filter((r) => r.id !== row.id);

      updateClient(
        UpdateClientBody.fromJS(toUpdateClient(getValues(), updatedUris)),
      );
    },
    [stableRedirectUris, updateClient, getValues],
  );

  const handleWebhooksOnChange = useCallback((webhooks: IClientWebhook[]) => {
    setEditedWebhooks(webhooks);
  }, []);

  useEffect(() => {
    setEditedWebhooks(webhooksData ?? []);
  }, [webhooksData]);

  const handleWebhookSave = useCallback(
    (webhook: IClientWebhook) => {
      const original = webhooksData?.find(
        (w: IClientWebhook) => w.id === webhook.id,
      );

      if (!original) {
        createWebhook(
          CreateAdminClientWebhookBody.fromJS({
            client_id: clientId,
            url: webhook.url,
            username: webhook.username,
            password: webhook.password,
          }),
        );
      } else {
        if (
          webhook.url !== original.url ||
          webhook.username !== original.username ||
          webhook.password !== original.password
        ) {
          updateWebhook({
            webhook_id: webhook.id,
            updateAdminClientWebhookBody: UpdateAdminClientWebhookBody.fromJS({
              url: webhook.url,
              username: webhook.username,
              password: webhook.password,
            }),
          });
        }
      }
    },
    [createWebhook, updateWebhook, clientId, webhooksData],
  );

  const handleWebhookDelete = useCallback(
    (webhook: IClientWebhook) => {
      const original = webhooksData?.find(
        (w: IClientWebhook) => w.id === webhook.id,
      );
      if (original) {
        deleteWebhook({ webhook_id: webhook.id });
      }
    },
    [deleteWebhook, webhooksData],
  );

  const handleApiKeySave = useCallback(
    (data: { api_key: string }) => {
      setApiKey(UpdateClientApiKeyBody.fromJS(data));
    },
    [setApiKey],
  );

  const handleCustomizationSave = useCallback(
    (values: Parameters<typeof UpdateClientCustomizationBody.fromJS>[0]) => {
      if (customizationData?.id) {
        updateCustomization(UpdateClientCustomizationBody.fromJS(values));
      } else {
        createCustomization(
          CreateClientCustomizationBody.fromJS({
            client_id: clientId,
            ...values,
          }),
        );
      }
    },
    [customizationData, updateCustomization, createCustomization, clientId],
  );

  const handleExport = () => {
    setExportDialogOpen(true);
  };

  const isClientLoading = isClientFetching || isUpdatingClient;
  const isClientDisabled = !isDirty;

  const isWebhooksLoading =
    isWebhooksFetching ||
    isUpdatingWebhook ||
    isDeletingWebhook ||
    isCreatingWebhook;

  const isApiKeyLoading = isApiKeyFetching || isApiKeyUpdatePending;

  const isCustomizationLoading =
    isCustomizationFetching ||
    isCreatingCustomization ||
    isUpdatingCustomization;

  const canUpdateClient = canExecuteAdminOperation(
    operationIds["core_admin.updateClient"],
  );
  const canUpdateClientApiKey = canExecuteAdminOperation(
    operationIds["core_admin.updateClientApiKey"],
  );
  const canCreateWebhook = canExecuteAdminOperation(
    operationIds["core_admin.createAdminClientWebhook"],
  );
  const canDeleteWebhook = canExecuteAdminOperation(
    operationIds["core_admin.deleteAdminClientWebhook"],
  );
  const canUpdateWebhook = canExecuteAdminOperation(
    operationIds["core_admin.updateAdminClientWebhook"],
  );

  const canShowApiKey = canExecuteAdminOperation(
    operationIds["core_admin.getClientApiKey"],
  );
  const canShowCustomization = canExecuteAdminOperation(
    operationIds["core_admin.getClientCustomization"],
  );

  const canVerifyOboAccount = canExecuteAdminOperation(
    operationIds["core.getClientOnboardingSession"],
  );

  const sideNavItems = useMemo<SectionSideNavItem[]>(() => {
    const items: SectionSideNavItem[] = [];
    if (canVerifyOboAccount) {
      items.push({ id: "account-status", label: "Account Status" });
    }

    items.push(
      { id: "general", label: "Client Settings" },
      { id: "redirects", label: "Redirect URIs" },
      { id: "webhooks", label: "Webhooks" },
      { id: "roles", label: "Roles & Permissions" },
    );
    if (canShowApiKey) {
      items.push({ id: "plugin-key", label: "Plugin Key" });
      items.push({ id: "api-key", label: "API Key" });
    }
    if (canShowCustomization) {
      items.push({ id: "branding", label: "Actions Page Customization" });
    }
    if (shouldShowAdminClientTimelines) {
      items.push({ id: "timelines", label: "Custom Timelines" });
    }
    items.push({ id: "configuration", label: "Configuration" });
    return items;
  }, [
    canShowApiKey,
    canShowCustomization,
    shouldShowAdminClientTimelines,
    canVerifyOboAccount,
  ]);

  return (
    <Box
      sx={{
        display: { xs: "block", md: "flex" },
        gap: { lg: 4.5 },
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: { xs: "contents", md: "block" },
          order: { md: 1 },
          flex: { md: "0 0 200px" },
          minWidth: 0,
          alignSelf: "stretch",
        }}
      >
        <SectionSideNav items={sideNavItems} />
      </Box>
      <Stack spacing={{ xs: 5, lg: 10 }} sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={{ xs: 3, lg: 5 }}>
          <Box id="account-status">
            <AccountStatusBanner
              clientId={clientId}
              connectedAccountId={
                clientData?.on_behalf_of_stripe_connected_account_id
              }
              isAdmin
            />
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              id="general"
              spacing={{ xs: 3, lg: 5 }}
              sx={{
                backgroundColor: "primary._states.hover",
                p: { xs: 2, sm: 3, lg: 5 },
                borderRadius: 2,
                scrollMarginTop: (theme) => theme.spacing(10),
              }}
            >
              <Stack spacing={1}>
                <HeadingContainer
                  title={
                    <>
                      <PublicSvgIcon name="code_blocks" /> Client Settings
                      <Tooltip
                        title={`Created: ${clientData?.created.toLocaleString(localization.locale, { timeZone: localization.timeZone })}
                      Updated: ${clientData?.updated.toLocaleString(localization.locale, { timeZone: localization.timeZone })}`}
                      >
                        <PublicSvgIcon name="info" />
                      </Tooltip>
                    </>
                  }
                  variant="h5"
                  borderType="solid"
                >
                  <IconButton
                    loading={isClientLoading}
                    variant="withBackground"
                    aria-label="Export JSON configuration"
                    onClick={handleExport}
                    data-testid="export-json-button"
                  >
                    <PublicSvgIcon name="ios_share" />
                  </IconButton>
                </HeadingContainer>
                {canExecuteAdminOperation(
                  operationIds["core_admin.getKeycloakClientSecret"],
                ) && (
                  <HideableText
                    value={keycloakClientSecretData?.client_secret ?? ""}
                    label="Keycloak Client Secret"
                    dataTestId="keycloak-client-secret-text"
                  />
                )}
              </Stack>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                justifyContent="space-between"
                spacing={{ xs: 2, lg: 5 }}
              >
                {renderField(
                  "id",
                  "Client ID",
                  "Client ID",
                  true,
                  !canUpdateClient,
                )}
                {renderField(
                  "name",
                  "Client Name",
                  "Client Name",
                  false,
                  !canUpdateClient,
                )}
                {renderField(
                  "contact_email",
                  "Contact Email",
                  "Contact Email",
                  false,
                  !canUpdateClient,
                )}
                {renderField(
                  "base_url",
                  "Base URL",
                  "Base URL",
                  false,
                  !canUpdateClient,
                )}
              </Stack>
              {canUpdateClient && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="flex-end"
                  spacing={1}
                >
                  <Button
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={isClientDisabled}
                    loading={isClientLoading}
                    fullWidth
                    sx={{ width: { sm: "auto" } }}
                    data-testid="save-client-settings-button"
                  >
                    Save client settings
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      refetchClient();
                    }}
                    disabled={isClientDisabled}
                    loading={isClientLoading}
                    fullWidth
                    sx={{ width: { sm: "auto" } }}
                    data-testid="cancel-changes-button"
                  >
                    Cancel changes
                  </Button>
                </Stack>
              )}
            </Stack>
          </form>
          <Button
            component={Link}
            to={`/admin/clients/${clientId}/fees`}
            variant="contained"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              alignSelf: "flex-end",
            }}
            size="large"
            data-testid="add-client-fees-button"
          >
            {canExecuteAdminOperation(
              operationIds["core_admin.updateClientFee"],
            ) ? (
              <>
                <PublicSvgIcon name="add_filled" inheritColor />
                Add custom client fees
              </>
            ) : (
              "Fee Structure"
            )}
          </Button>
          <Box
            id="redirects"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <RedirectURIsEditorWithIds
              value={stableRedirectUris}
              onChange={setStableRedirectUris}
              loading={isClientLoading}
              canDelete={canUpdateRedirectURIs}
              canEdit={canUpdateRedirectURIs}
              canAdd={canUpdateRedirectURIs}
              zodSchema={clientRedirectUriSchema}
              redirectUriTooltipMessage="Client-added Redirect URIs must match the Base URL domain. Trustap Admins can override this and authorize external domains."
              onSave={handleRedirectUriSave}
              onDelete={handleRedirectUriDelete}
            />
          </Box>
        </Stack>

        <Box
          id="webhooks"
          sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
        >
          <WebhooksEditor
            onChange={handleWebhooksOnChange}
            value={editedWebhooks}
            isLoading={isWebhooksLoading}
            canAdd={canCreateWebhook}
            canEdit={canUpdateWebhook}
            canDelete={canDeleteWebhook}
            onSave={handleWebhookSave}
            onDelete={handleWebhookDelete}
          />
        </Box>

        <Box id="roles" sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}>
          <RolesAndPermissions
            clientId={clientId}
            clientName={clientData?.name}
            isAdmin
          />
        </Box>

        <Stack spacing={{ xs: 3, lg: 5 }}>
          {canExecuteAdminOperation(
            operationIds["core_admin.getClientApiKey"],
          ) && (
            <Grid
              container
              spacing={{ xs: 3, lg: 6 }}
              sx={{
                backgroundColor: "primary._states.hover",
                borderRadius: 2,
                height: "max-content",
                p: { xs: 2, lg: 0 },
              }}
            >
              <Grid
                sx={{
                  pl: { xs: 0, lg: 6 },
                  pt: { xs: 0, lg: 6 },
                  py: { xs: 0, lg: 6 },
                  scrollMarginTop: (theme) => theme.spacing(10),
                }}
                size={{ xs: 12, lg: 6 }}
                id="plugin-key"
              >
                <Stack spacing={{ xs: 3, lg: 5 }}>
                  <Typography variant="h5SolidDivider">
                    <PublicSvgIcon name="vpn_key_alert" /> Plugin Key&nbsp;
                    <Tooltip title="This key is relevant only to WooCommerce or Shopify sellers who use the Trustap Payments plugin. Please input it in the plugin settings.">
                      <PublicSvgIcon name="info" />
                    </Tooltip>
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setEncodedDialog(true)}
                    fullWidth
                    sx={{ width: { lg: "auto" } }}
                    data-testid="view-plugin-key-button"
                  >
                    View Plugin Key
                  </Button>
                </Stack>
              </Grid>
              <Grid
                sx={{
                  pr: { xs: 0, lg: 6 },
                  pt: { xs: 0, lg: 6 },
                  py: { xs: 0, lg: 6 },
                  scrollMarginTop: (theme) => theme.spacing(10),
                }}
                size={{ xs: 12, lg: 6 }}
                id="api-key"
              >
                <ApiKeyEditor
                  apiKey={apiKeyData?.api_key}
                  isLoading={isApiKeyLoading}
                  canEdit={canUpdateClientApiKey}
                  onSave={handleApiKeySave}
                />
              </Grid>
            </Grid>
          )}
          {(shouldShowAdminClientTimelines ||
            canExecuteAdminOperation(
              operationIds["core_admin.getClientCustomization"],
            )) && (
            <Grid
              container
              spacing={{ xs: 3, lg: 6 }}
              sx={{
                backgroundColor: "primary._states.hover",
                borderRadius: 2,
                height: "max-content",
                p: { xs: 2, lg: 0 },
              }}
            >
              {canExecuteAdminOperation(
                operationIds["core_admin.getClientCustomization"],
              ) && (
                <Grid
                  sx={{
                    pl: { xs: 0, lg: 6 },
                    pt: { xs: 0, lg: 6 },
                    py: { xs: 0, lg: 6 },
                    scrollMarginTop: (theme) => theme.spacing(10),
                  }}
                  size={{ xs: 12, lg: 6 }}
                  id="branding"
                >
                  <CustomizationEditor
                    customizationData={customizationData}
                    isLoading={isCustomizationLoading}
                    canEdit={canExecuteAdminOperation(
                      operationIds["core_admin.updateClientCustomization"],
                    )}
                    onSave={
                      canExecuteAdminOperation(
                        operationIds["core_admin.updateClientCustomization"],
                      )
                        ? handleCustomizationSave
                        : undefined
                    }
                  />
                </Grid>
              )}
              {shouldShowAdminClientTimelines && (
                <Grid
                  sx={{
                    pr: { xs: 0, lg: 6 },
                    pt: { xs: 0, lg: 6 },
                    py: { xs: 0, lg: 6 },
                    scrollMarginTop: (theme) => theme.spacing(10),
                  }}
                  size={{ xs: 12, lg: 6 }}
                  id="timelines"
                >
                  <Stack spacing={{ xs: 3, lg: 5 }}>
                    <Typography variant="h5SolidDivider">
                      <PublicSvgIcon name="history_2" /> Custom Timelines
                    </Typography>
                    {canExecuteAdminOperation(
                      operationIds["core_admin.getAdminClientTimelines"],
                    ) && (
                      <OnlineTimelines
                        clientId={clientId}
                        mode={
                          canExecuteAdminOperation(
                            operationIds["core_admin.updateClientTimelines"],
                          )
                            ? "edit"
                            : "view"
                        }
                      />
                    )}
                    {canExecuteAdminOperation(
                      operationIds["core_admin.getAdminClientF2fTimelines"],
                    ) && (
                      <F2FTimelines
                        clientId={clientId}
                        mode={
                          canExecuteAdminOperation(
                            operationIds["core_admin.updateClientF2fTimelines"],
                          )
                            ? "edit"
                            : "view"
                        }
                      />
                    )}
                  </Stack>
                </Grid>
              )}
            </Grid>
          )}
        </Stack>
        <Stack
          id="configuration"
          sx={{
            backgroundColor: "primary._states.hover",
            borderRadius: 2,
            height: "max-content",
            p: { xs: 2, lg: 0 },
            scrollMarginTop: (theme) => theme.spacing(10),
          }}
        >
          <ClientMetadataEditor clientId={clientId} canEdit={canUpdateClient} />
        </Stack>

        {canExecuteAdminOperation(
          operationIds["core_admin.getClientApiKey"],
        ) && (
          <EncodedClientKeyDialog
            open={isEncodedDialogOpened}
            onClose={() => setEncodedDialog(false)}
            webhooks={webhooksData ?? []}
            apiKey={apiKeyData?.api_key}
            clientSecret={keycloakClientSecretData?.client_secret}
            clientId={clientId}
          />
        )}

        {canExecuteAdminOperation(operationIds["core_admin.encrypt"]) && (
          <ClientExportDialog
            open={isExportDialogOpen}
            onClose={() => setExportDialogOpen(false)}
            clientData={
              toUpdateClient(
                getValues(),
                stableRedirectUris,
              ) as IClientResponse2
            }
            clientId={clientId}
          />
        )}
      </Stack>
    </Box>
  );
}
